import axios from 'axios';
import { BASE_URL } from '../apis/api';

const API_BASE_URL = BASE_URL;
const PHP_GATEWAY_URL = process.env.REACT_APP_PHP_GATEWAY_URL || 'https://keralasahityaakademi.org/sbiepay-php/public';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

class PaymentService {
  
  // Initiate payment process - ENHANCED VERSION
  async initiatePayment(paymentData) {
    try {
      // Step 1: Create payment record in Node.js backend
      const response = await api.post('/api/payments/initiate', {
        amount: parseFloat(paymentData.amount),
        customerEmail: paymentData.customerEmail,
        customerPhone: paymentData.customerPhone,
        customerName: paymentData.customerName,
        description: paymentData.description || 'Payment',
        userId: paymentData.userId,
        metadata: paymentData.metadata || {}
      });

      if (response.data.success) {
        const paymentRecord = response.data.data;
        
        // Step 2: Store payment data for tracking
        const paymentSession = {
          paymentId: paymentRecord.paymentId,
          orderId: paymentRecord.orderId,
          amount: paymentRecord.amount,
          timestamp: new Date().toISOString(),
          ...paymentData
        };
        
        sessionStorage.setItem('currentPayment', JSON.stringify(paymentSession));
        
        // Step 3: Try multiple redirect methods for maximum compatibility
        await this.redirectToPaymentGateway(paymentSession);
        
        return response.data;
      } else {
        throw new Error(response.data.error || 'Payment initiation failed');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      throw new Error(
        error.response?.data?.error || 
        error.message || 
        'Failed to initiate payment'
      );
    }
  }

  // Enhanced redirect method with fallbacks
  async redirectToPaymentGateway(paymentData) {
    const methods = ['POST', 'GET', 'WINDOW_OPEN'];
    
    // Show loading overlay first
    this.showRedirectMessage();
    
    // Try POST method first (most reliable)
    try {
      await this.redirectViaPost(paymentData);
      return;
    } catch (error) {
      console.warn('POST redirect failed, trying GET method:', error);
    }
    
    // Fallback to GET method
    try {
      await this.redirectViaGet(paymentData);
      return;
    } catch (error) {
      console.warn('GET redirect failed, trying window.open:', error);
    }
    
    // Final fallback - open in new window
    this.redirectViaNewWindow(paymentData);
  }

  // Method 1: POST form submission (most reliable)
  async redirectViaPost(paymentData) {
    return new Promise((resolve, reject) => {
      try {
        // Clean and prepare data
        const cleanData = this.preparePaymentData(paymentData);
        
        // Create form element
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${PHP_GATEWAY_URL}/payment-form-direct-test.php`; // Use your working file name
        form.style.display = 'none';
        form.enctype = 'application/x-www-form-urlencoded';

        // Add form fields
        Object.keys(cleanData).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = cleanData[key];
          form.appendChild(input);
        });

        // Add form to document and submit
        document.body.appendChild(form);
        
        console.log('Submitting payment form via POST:', cleanData);
        
        // Submit after short delay to ensure DOM is ready
        setTimeout(() => {
          form.submit();
          resolve();
        }, 500);
        
      } catch (error) {
        reject(error);
      }
    });
  }

  // Method 2: GET redirect with URL parameters
  async redirectViaGet(paymentData) {
    return new Promise((resolve, reject) => {
      try {
        // Clean and prepare data
        const cleanData = this.preparePaymentData(paymentData);
        
        // Create URL with parameters
        const params = new URLSearchParams();
        Object.keys(cleanData).forEach(key => {
          params.append(key, cleanData[key]);
        });
        
        const redirectUrl = `${PHP_GATEWAY_URL}/payment-form-direct-test.php?${params.toString()}`;
        
        console.log('Redirecting via GET:', redirectUrl);
        
        // Direct redirect
        setTimeout(() => {
          window.location.href = redirectUrl;
          resolve();
        }, 500);
        
      } catch (error) {
        reject(error);
      }
    });
  }

  // Method 3: Open in new window (fallback)
  redirectViaNewWindow(paymentData) {
    try {
      const cleanData = this.preparePaymentData(paymentData);
      const params = new URLSearchParams(cleanData);
      const redirectUrl = `${PHP_GATEWAY_URL}/payment-form-direct-test.php?${params.toString()}`;
      
      console.log('Opening payment in new window:', redirectUrl);
      
      // Close redirect overlay
      this.hideRedirectMessage();
      
      // Open in new window
      const paymentWindow = window.open(
        redirectUrl, 
        'sbipay_payment', 
        'width=800,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no'
      );
      
      if (!paymentWindow) {
        alert('Please allow popups for this site to complete your payment.');
        // Fallback to same window
        window.location.href = redirectUrl;
      }
      
    } catch (error) {
      console.error('New window redirect failed:', error);
      alert('Payment redirect failed. Please try again.');
      this.hideRedirectMessage();
    }
  }

  // Prepare and clean payment data
  preparePaymentData(paymentData) {
    return {
      orderId: String(paymentData.orderId || '').trim(),
      amount: String(paymentData.amount || '0').trim(),
      customerEmail: String(paymentData.customerEmail || '').trim(),
      customerPhone: String(paymentData.customerPhone || '').replace(/[^\d]/g, ''), // Remove non-digits
      customerName: String(paymentData.customerName || '').trim(),
      userId: String(paymentData.userId || '').trim(),
      reactAppUrl: String(window.location.origin).trim(),
      nodeBackendUrl: String(API_BASE_URL.replace('/api', '')).trim(),
      description: String(paymentData.description || 'Payment').trim()
    };
  }

  // Enhanced redirect message with progress
  showRedirectMessage() {
    // Remove existing overlay
    this.hideRedirectMessage();

    const overlay = document.createElement('div');
    overlay.id = 'payment-redirect-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      color: white;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    `;

    overlay.innerHTML = `
      <div style="text-align: center; padding: 40px; background: rgba(255,255,255,0.1); border-radius: 20px; backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.2); max-width: 450px; width: 90%;">
        <div style="font-size: 60px; margin-bottom: 20px; animation: pulse 2s infinite;">🔒</div>
        <h2 style="margin: 0 0 15px 0; font-weight: 300; font-size: 28px;">Redirecting to Payment Gateway</h2>
        <p style="margin: 0 0 25px 0; opacity: 0.9; font-size: 16px;">Please wait while we securely redirect you to SBIePay...</p>
        
        <div style="margin: 20px 0;">
          <div style="width: 50px; height: 50px; border: 4px solid rgba(255,255,255,0.3); border-top: 4px solid white; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto;"></div>
        </div>
        
        <div id="redirect-progress" style="margin: 20px 0; font-size: 14px; opacity: 0.8;">
          Preparing payment data...
        </div>
        
        <div style="margin-top: 25px; padding: 15px; background: rgba(255,255,255,0.1); border-radius: 10px; font-size: 14px;">
          <span style="font-size: 20px; margin-right: 8px;">🛡️</span>
          <strong>Secure Transaction:</strong> Your payment is protected by SSL encryption
        </div>
        
        <button id="manual-redirect" style="
          background: rgba(255,255,255,0.2);
          border: 2px solid white;
          color: white;
          padding: 12px 24px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
          margin-top: 20px;
          display: none;
        ">Click here if not redirected automatically</button>
      </div>
      
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      </style>
    `;

    document.body.appendChild(overlay);

    // Show manual redirect button after 5 seconds
    setTimeout(() => {
      const manualButton = document.getElementById('manual-redirect');
      if (manualButton) {
        manualButton.style.display = 'inline-block';
        manualButton.onclick = () => {
          const currentPayment = this.getCurrentPaymentSession();
          if (currentPayment) {
            this.redirectViaGet(currentPayment);
          }
        };
      }
    }, 5000);

    // Remove overlay after 30 seconds
    setTimeout(() => {
      this.hideRedirectMessage();
    }, 30000);
  }

  // Update progress message
  updateRedirectProgress(message) {
    const progressElement = document.getElementById('redirect-progress');
    if (progressElement) {
      progressElement.textContent = message;
    }
  }

  // Hide redirect message
  hideRedirectMessage() {
    const overlay = document.getElementById('payment-redirect-overlay');
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
  }

  // Test PHP gateway connectivity
  async testGatewayConnection() {
    try {
      console.log('Testing gateway connection...');
      
      const testUrl = `${PHP_GATEWAY_URL}/payment-form-direct-test.php?test=connection`;
      const response = await fetch(testUrl, {
        method: 'GET',
        mode: 'no-cors' // Allow cross-origin for testing
      });
      
      console.log('Gateway connection test completed');
      return true; // no-cors mode always returns opaque response
    } catch (error) {
      console.error('Gateway connection test failed:', error);
      return false;
    }
  }

  // Enhanced validation with better error messages
  validatePaymentData(data) {
    const errors = {};

    // Amount validation
    const amount = parseFloat(data.amount);
    if (!data.amount || isNaN(amount) || amount <= 0) {
      errors.amount = 'Amount must be a valid number greater than 0';
    } else if (amount > 500000) {
      errors.amount = 'Amount cannot exceed ₹5,00,000';
    } else if (amount < 1) {
      errors.amount = 'Minimum amount is ₹1';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.customerEmail || !emailRegex.test(data.customerEmail.trim())) {
      errors.customerEmail = 'Please enter a valid email address';
    }

    // Phone validation (Indian format)
    const phoneRegex = /^[6-9]\d{9}$/;
    const cleanPhone = (data.customerPhone || '').replace(/[^\d]/g, '');
    if (!cleanPhone || !phoneRegex.test(cleanPhone)) {
      errors.customerPhone = 'Please enter a valid 10-digit Indian mobile number';
    }

    // Name validation
    if (data.customerName && data.customerName.trim().length < 2) {
      errors.customerName = 'Name must be at least 2 characters long';
    }

    // User ID validation
    if (!data.userId) {
      errors.userId = 'User ID is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Get payment details by order ID
  async getPayment(orderId) {
    try {
      const response = await api.get(`/payments/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Get payment error:', error);
      throw new Error(
        error.response?.data?.error || 
        'Failed to retrieve payment details'
      );
    }
  }

  // Get payment history for current user
  async getPaymentHistory(page = 1, limit = 10, status = null) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (status) {
        params.append('status', status);
      }

      const response = await api.get(`/payments/user/history?${params}`);
      return response.data;
    } catch (error) {
      console.error('Get payment history error:', error);
      throw new Error(
        error.response?.data?.error || 
        'Failed to retrieve payment history'
      );
    }
  }

  // Verify payment status
  async verifyPayment(orderId) {
    try {
      const response = await api.post(`/payments/${orderId}/verify`);
      return response.data;
    } catch (error) {
      console.error('Payment verification error:', error);
      throw new Error(
        error.response?.data?.error || 
        'Failed to verify payment'
      );
    }
  }

  // Cancel pending payment
  async cancelPayment(orderId, reason = '') {
    try {
      const response = await api.post(`/payments/${orderId}/cancel`, {
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Payment cancellation error:', error);
      throw new Error(
        error.response?.data?.error || 
        'Failed to cancel payment'
      );
    }
  }

  // Get payment statistics
  async getPaymentStats(startDate = null, endDate = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(`/payments/stats/summary?${params}`);
      return response.data;
    } catch (error) {
      console.error('Payment stats error:', error);
      throw new Error(
        error.response?.data?.error || 
        'Failed to retrieve payment statistics'
      );
    }
  }

  // Utility methods
  
  // Format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  // Get payment status color
  getStatusColor(status) {
    const colors = {
      pending: '#ffc107',
      processing: '#17a2b8',
      completed: '#28a745',
      failed: '#dc3545',
      cancelled: '#6c757d',
      refunded: '#fd7e14',
      partial_refund: '#e83e8c'
    };
    return colors[status] || '#6c757d';
  }

  // Get payment status icon
  getStatusIcon(status) {
    const icons = {
      pending: '⏳',
      processing: '🔄',
      completed: '✅',
      failed: '❌',
      cancelled: '🚫',
      refunded: '↩️',
      partial_refund: '↪️'
    };
    return icons[status] || '❓';
  }

  // Get current payment session
  getCurrentPaymentSession() {
    const session = sessionStorage.getItem('currentPayment');
    return session ? JSON.parse(session) : null;
  }

  // Clear payment session
  clearPaymentSession() {
    sessionStorage.removeItem('currentPayment');
    this.hideRedirectMessage();
  }

  // Check if payment is in progress
  isPaymentInProgress() {
    const session = this.getCurrentPaymentSession();
    if (!session) return false;
    
    // Check if payment session is not older than 1 hour
    const sessionTime = new Date(session.timestamp);
    const now = new Date();
    const hoursDiff = (now - sessionTime) / (1000 * 60 * 60);
    
    return hoursDiff < 1;
  }
}

export default new PaymentService();